import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineMapPin,
  HiOutlineHome,
  HiOutlineBriefcase,
  HiOutlineXMark,
  HiOutlinePencilSquare,
  HiOutlineTrash,
  HiOutlinePlus,
  HiOutlineCheckCircle, // Added for selection indicator
} from "react-icons/hi2";
import toast from "react-hot-toast";
import SummaryApi from "../common";

const UserProfileAddress = ({ userId }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null); // New state for selection
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [typeError, setTypeError] = useState("");

  const [formData, setFormData] = useState({
    user_name: "",
    user_mobile: "",
    flat_number: "",
    street_name: "",
    land_mark: "",
    address_type: "",
    city: "",
    state: "",
    pincode: "",
    address: "",
    is_default: 0,
  });

  const fetchAddresses = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await fetch(SummaryApi.getUserAddressbyId.url, {
        method: SummaryApi.getUserAddressbyId.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await response.json();
      if (result.status) {
        const data = result.data || [];
        setAddresses(data);
        
        // Auto-select the primary (default) address on load
        const primary = data.find(a => a.is_default === 1);
        if (primary) setSelectedId(primary.address_id);
      }
    } catch (error) {
      console.error("Failed to load addresses", error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const handleTypeChange = (val) => {
    setFormData((prev) => ({ ...prev, address_type: val }));
    const isDuplicate = addresses.some(
      (addr) =>
        addr.address_type.toLowerCase() === val.toLowerCase() &&
        addr.address_id !== formData.address_id
    );
    if (isDuplicate && val.trim() !== "") {
      setTypeError("address name is already used. Please choose another");
    } else {
      setTypeError("");
    }
  };

  const handleOpenModal = (address = null) => {
    setTypeError("");
    if (address) {
      setEditMode(true);
      setFormData({ ...address });
    } else {
      setEditMode(false);
      setFormData({
        user_name: "",
        user_mobile: "",
        flat_number: "",
        street_name: "",
        land_mark: "",
        address_type: "",
        city: "",
        state: "",
        pincode: "",
        address: "",
        is_default: addresses.length === 0 ? 1 : 0,
      });
    }
    setIsModalOpen(true);
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    if (typeError) return;
    const isEditing = !!formData.address_id;
    const apiConfig = isEditing ? SummaryApi.addressUpdate : SummaryApi.addressCreate;

    try {
      const response = await fetch(apiConfig.url, {
        method: apiConfig.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, user_id: userId }),
      });
      const result = await response.json();
      if (result.status) {
        toast.success(isEditing ? "Address updated!" : "Address saved!");
        fetchAddresses();
        setIsModalOpen(false);
      } else {
        toast.error(result.message || "Failed to save address");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm("Are you sure you want to delete this address?")) return;
    try {
      const response = await fetch(SummaryApi.addressDelete.url, {
        method: SummaryApi.addressDelete.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address_id: addressId }),
      });
      const result = await response.json();
      if (result.status) {
        toast.success("Address removed");
        if (selectedId === addressId) setSelectedId(null);
        fetchAddresses();
      }
    } catch (error) {
      toast.error("Error deleting address");
    }
  };

  return (
    <div className="bg-white/70 backdrop-blur-md border border-white p-8 md:p-10 rounded-[2.5rem] shadow-xl">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-8 px-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-rose-500 rounded-lg text-white">
            <HiOutlineMapPin size={20} />
          </div>
          <div>
            <h3 className="text-rose-950 font-black uppercase tracking-widest text-sm">
              Your Address Book
            </h3>
            <p className="text-[10px] text-rose-400 font-bold uppercase mt-0.5">
              Select or manage delivery locations
            </p>
          </div>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-rose-50 text-rose-500 hover:bg-rose-500 hover:text-white px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-sm border border-rose-100"
        >
          + Add New Address
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-rose-500"></div>
        </div>
      ) : addresses.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-rose-100 rounded-[2rem]">
            <p className="text-rose-300 font-bold uppercase text-xs">No addresses saved yet</p>
        </div>
      ) : (
        /* HORIZONTAL SCROLL CONTAINER */
        <div className="flex overflow-x-auto gap-6 pb-6 custom-scrollbar scroll-smooth px-2 ">
          {addresses.map((addr) => (
            <motion.div
              layout
              key={addr.address_id}
              onClick={() => setSelectedId(addr.address_id)} // Click to select
              className={`flex-shrink-0 w-[300px] relative cursor-pointer group p-6 rounded-[2.2rem] border-2 transition-all duration-300 ${
                selectedId === addr.address_id
                  ? "border-rose-500 bg-white shadow-xl shadow-rose-100/50 "
                  : "border-gray-100 bg-white/50 hover:border-rose-200"
              }`}
            >
              {/* Type Badge & Indicators */}
              <div className="flex justify-between items-center mb-4">
                <div className="p-2 bg-rose-50 rounded-lg text-rose-500">
                  {addr.address_type?.toLowerCase() === "home" ? (
                    <HiOutlineHome size={18} />
                  ) : (
                    <HiOutlineBriefcase size={18} />
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                   {addr.is_default === 1 && (
                    <span className="bg-rose-100 text-rose-600 text-[8px] font-black uppercase px-2 py-1 rounded-md">
                      Primary
                    </span>
                  )}
                  {selectedId === addr.address_id && (
                    <HiOutlineCheckCircle className="text-rose-500" size={22} />
                  )}
                </div>
              </div>

              {/* Identity Section */}
              <div className="space-y-1 mb-4">
                <p className="text-rose-950 font-black text-sm uppercase truncate">
                  {addr.user_name}
                </p>
                <p className="text-rose-500 font-bold text-[11px]">
                  {addr.user_mobile}
                </p>
              </div>

              <div className="h-px bg-gray-100 w-full mb-4" />

              {/* Physical Address Section */}
              <div className="space-y-1">
                <p className="text-gray-500 text-[11px] font-semibold leading-relaxed line-clamp-2">
                  {addr.flat_number}, {addr.street_name}, {addr.city}, {addr.state}
                </p>
                <p className="text-rose-950 font-black text-sm mt-3 tracking-[0.2em]">
                  {addr.pincode}
                </p>
              </div>

              {/* Edit/Delete Actions (Appear on Hover) */}
              <div className="absolute top-4 right-4 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.stopPropagation(); handleOpenModal(addr); }}
                  className="p-1.5 bg-blue-50 text-blue-500 rounded-lg hover:bg-blue-500 hover:text-white transition-all shadow-sm"
                >
                  <HiOutlinePencilSquare size={14} />
                </button>
                {!addr.is_default && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteAddress(addr.address_id); }}
                    className="p-1.5 bg-rose-50 text-rose-500 rounded-lg hover:bg-rose-500 hover:text-white transition-all shadow-sm"
                  >
                    <HiOutlineTrash size={14} />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* MODAL (Add/Edit) */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsModalOpen(false)} className="absolute inset-0 bg-rose-950/40 backdrop-blur-sm" />
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="relative bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-[3.5rem] shadow-2xl p-10" >
              <button onClick={() => setIsModalOpen(false)} className="absolute top-10 right-10 text-rose-300 hover:text-rose-500"><HiOutlineXMark size={28} /></button>
              <h2 className="text-3xl font-black text-rose-950 uppercase tracking-tighter mb-8">{editMode ? "Edit Address" : "Add New Address"}</h2>
              
              <form onSubmit={handleAddressSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <FormInput label="Full Name" value={formData.user_name} onChange={(v) => setFormData({ ...formData, user_name: v })} required />
                  <FormInput label="Mobile" type="tel" maxLength={10} value={formData.user_mobile} onChange={(v) => setFormData({ ...formData, user_mobile: v.replace(/\D/g, "") })} required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FormInput label="Flat / House No" value={formData.flat_number} onChange={(v) => setFormData({ ...formData, flat_number: v })} required />
                  <FormInput label="Street Name" value={formData.street_name} onChange={(v) => setFormData({ ...formData, street_name: v })} required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FormInput label="Landmark" value={formData.land_mark} onChange={(v) => setFormData({ ...formData, land_mark: v })} />
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-black text-rose-400 uppercase tracking-widest ml-1">Type (Home, Work...)</label>
                    <input className={`w-full bg-rose-50/50 border ${typeError ? 'border-red-400' : 'border-rose-100'} rounded-2xl p-4 text-sm text-rose-950 font-bold outline-none focus:ring-2 ring-rose-200`} placeholder="Enter address name" value={formData.address_type} onChange={(e) => handleTypeChange(e.target.value)} required />
                    {typeError && <span className="text-[9px] text-red-500 font-bold ml-1">{typeError}</span>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <FormInput label="City" value={formData.city} onChange={(v) => setFormData({ ...formData, city: v })} required />
                  <FormInput label="State" value={formData.state} onChange={(v) => setFormData({ ...formData, state: v })} required />
                  <FormInput label="Pincode" type="tel" maxLength={6} value={formData.pincode} onChange={(v) => setFormData({ ...formData, pincode: v.replace(/\D/g, "") })} required />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-black text-rose-400 uppercase tracking-widest ml-1">Full Address</label>
                  <textarea rows="3" className="w-full bg-rose-50/50 border border-rose-100 rounded-2xl p-4 text-sm text-rose-950 font-bold outline-none focus:ring-2 ring-rose-200" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                </div>
                <button type="submit" disabled={!!typeError} className={`w-full ${typeError ? 'bg-gray-300' : 'bg-rose-600 hover:bg-rose-700'} text-white py-5 rounded-[2rem] font-black uppercase text-xs tracking-widest shadow-xl transition-all active:scale-95 mt-4`} >
                  {editMode ? "Update Address" : "Save Address"}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #fb7185; border-radius: 10px; }
      `}</style>
    </div>
  );
};

const FormInput = ({ label, value, onChange, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-black text-rose-400 uppercase tracking-widest ml-1">{label}</label>
    <input {...props} className="w-full bg-rose-50/50 border border-rose-100 rounded-2xl p-4 text-sm text-rose-950 font-bold outline-none focus:ring-2 ring-rose-200" value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

export default UserProfileAddress;